<template>
	<div class="relative h-full pt-1 md:py-4">
		<div
			v-if="hideCarousel"
			class="bg-[#035046] absolute bottom-0 z-40 w-full h-full"
		/>
		<WwCarousel
			v-if="hasDabDays"
			:id="DABVENT_CAROUSEL"
			:ref="DABVENT_CAROUSEL"
			:item-count="EVENT_DAY_COUNT"
			:items-per-slide="1"
			side-scroller-class="pb-6 snap-x snap-mandatory scroll-smooth"
			scrollbar-visibility="hide-horizontal-scrollbar"
			arrow-visibility="hidden xl:block"
			fade-visibility="hidden"
			arrow-offset-classes="mb-6"
			arrow-next-offset="mr-[-30px]"
			arrow-previous-offset="mr-[105px]"
			arrow-size-classes="w-10 h-10"
			arrow-classes="ring-offset-2 hover:ring focus:ring ring-[#f7dc4f] focus:border-[#f7dc4f] bg-[#f7dc4f]"
			hide-footer
			hide-header
			emit-scroll
			:initial-slide="initialSlide"
			:scroll-behavior="scrollBehavior"
			@handle-scroll="handleScroll"
		>
			<template #slides>
				<div
					id="scroller-inner"
					:ref="DABVENT_SCROLLER_INNER"
					class="flex mr-2 py-14"
					:style="{ marginLeft: setScrollerMargin(LEFT), marginRight: setScrollerMargin(RIGHT) }"
				>
					<DabventCarouselItem
						v-for="(_, index) in EVENT_CARDS"
						:id="CAROUSEL_ITEM"
						:key="index"
						:index="index"
						:active-index="intersectingIndex"
						:dab-day="dabventDays[index]"
						:total-count="EVENT_DAY_COUNT"
						:is-desktop="isDesktop"
						@set-active-slide="handleClick"
					/>
				</div>
			</template>
		</WwCarousel>
		<div v-else>
			<span
				class="cursor-pointer"
				@click="dabventReset"
			>
				Refresh event
			</span>
		</div>
		<DabventModal :modal-data="modalData" />
	</div>
</template>

<script>
import { mapGetters } from 'vuex'

import DabventCarouselItem from '@/components/dabvent/DabventCarouselItem.vue'
import DabventModal from '@/components/UI/modals/DabventModal.vue'
import WwCarousel from '@/components/UI/WwCarousel.vue'
import { useDabvent } from '@/composables/dabvent.js'
import CAROUSEL_ITEM from '@/constants/carousel/carousel-item.js'
import { EVENT_CARDS, EVENT_DAY_COUNT, EVENT_DETAILS } from '@/constants/dabvent/event-details.js'
import SIDE_SCROLLER from '@/constants/UI/side-scroller.js'

const CARD_WIDTH = 208
const LEFT = 'left'
const RIGHT = 'right'
const DABVENT_SCROLLER_INNER = 'dabventScroller'
const DABVENT_CAROUSEL = 'dabventCarousel'

const { dabventDays, getDayOfEvent, reset } = useDabvent()

export default {
	components: {
		WwCarousel,
		DabventCarouselItem,
		DabventModal
	},
	props: {
		scrollBehavior: {
			type: String,
			default: 'smooth',
			validator: (value) => {
				return [ 'smooth', 'auto' ].includes(value)
			}
		},
		eventDetails: {
			type: Object,
			required: true
		}
	},
	data() {
		return {
			isMounted: false,
			observer: null,
			scrollTargets: null,
			intersectingIndex: 0,
			scrollerContainer: null,
			allowScroll: true,
			LEFT,
			RIGHT,
			EVENT_DAY_COUNT,
			EVENT_CARDS,
			DABVENT_SCROLLER_INNER,
			DABVENT_CAROUSEL,
			CAROUSEL_ITEM,
			CARD_WIDTH,
			initialSlide: 1,
			hideCarousel: true
		}
	},
	computed: {
		...mapGetters([ 'isMobile', 'isDesktop' ]),
		browserSupportsIO() {
			return window && 'IntersectionObserver' in window
		},
		modalData() {
			return this.EVENT_CARDS[this.intersectingIndex] || {}
		},
		dabventDays() {
			return dabventDays.value
		},
		hasDabDays() {
			return this.dabventDays.length > 0
		}
	},
	watch: {
		isDesktop: { // set slide index for arrow control when screen size changes to desktop from mobile
			handler(newVal) {
				if (newVal && this.$refs?.[DABVENT_SCROLLER_INNER]) {
					this.$refs[DABVENT_CAROUSEL].setSlide(this.intersectingIndex)
				}
			},
			immediate: true
		}
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.resizeHandler)
		if (this.observer) {
			this.observer.disconnect()
		}
	},
	mounted() {
		this.$nextTick(function () {
			// Code that will run only after the
			// entire view has been rendered
			this.isMounted = true
			window.addEventListener('resize', this.resizeHandler)

			const interval = setInterval(() => { // hack to wait for carousel to mount
				if (this.$refs?.[DABVENT_SCROLLER_INNER]) {
					this.getScrollContainer() // get measurements
					this.startIO() // start watching for intersection
					this.setInitialScrollPosition() // move to initial position and remove invisible
					clearInterval(interval) // stop the interval
				}
			}, 50)
		})
	},
	methods: {
		dabventReset() {
			reset(EVENT_DETAILS)
		},
		setInitialSlide() { // carousel slides are 1-indexed
			this.initialSlide = getDayOfEvent(this.eventDetails) + 1 || 1
		},
		resizeHandler() { // re-caluclate measurements on resize
			if (this.isMounted && this.$refs?.[DABVENT_SCROLLER_INNER]) {
				this.$refs[DABVENT_SCROLLER_INNER].style.marginLeft = this.setScrollerMargin(LEFT)
				this.$refs[DABVENT_SCROLLER_INNER].style.marginRight = this.setScrollerMargin(RIGHT)
				this.getScrollContainer()
			}
		},
		handleClick(index) {
			if (index && !this.isDesktop) {
				this.allowScroll = false
				this.getScrollContainer()

				this.scrollerContainer.scrollTo({ // scroll on click for mobile only
					top: 0,
					left: this.getLeftEdgePosition(index),
					behavior: 'smooth'
				})

				setTimeout(() => {
					this.allowScroll = true
				}, 1000)

				this.intersectingIndex = index
			}
		},
		getLeftEdgePosition(index) {
			return (index * CARD_WIDTH) - (CARD_WIDTH / 2)
		},
		setInitialScrollPosition() {
			// Set scroll position based on the next un-opened card
			this.setInitialSlide()
			setTimeout(() => {
				this.scrollerContainer.scrollLeft = this.getLeftEdgePosition(this.initialSlide) || 0
			}, 50)
			setTimeout(() => {
				this.hideCarousel = false
			}, 200)
		},
		startIO() {
			this.scrollTargets = Array.from(document.querySelectorAll(`#${CAROUSEL_ITEM}`))
			this.observer = new IntersectionObserver((entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						this.intersectingIndex = this.scrollTargets.indexOf(entry.target)
					}
				})
			}, {
				root: this.scrollerContainer,
				rootMargin: '0px -50% 0px -50%', // sets our IO's window of observervation to the horizontal center of the element
				threshold: 0
			})
			for (const element of this.scrollTargets) {
				this.observer.observe(element)
			}
		},
		setScrollerMargin(direction) { // sets the left and right margins of the scrollerContainer inner element to center the cards on the screen
			let margin = 0
			if (this.isMounted) {
				const screenWidth = window.innerWidth
				const containerWidth = this.scrollerContainer.clientWidth
				const width = containerWidth < screenWidth ? containerWidth : screenWidth

				if (direction === LEFT) {
					margin = (width / 2)
				} else {
					margin = width / 2 + CARD_WIDTH * EVENT_DAY_COUNT
				}
			}

			return `${margin}px`
		},
		getScrollContainer() {
			this.scrollerContainer = this.$refs?.[DABVENT_CAROUSEL]?.$children?.[0]?.$refs?.[SIDE_SCROLLER]
		},
		handleScroll(activeSlide) {
			if (this.allowScroll) {
				this.scrollerContainer.scrollTo({
					top: 0,
					left: this.getLeftEdgePosition(activeSlide),
					behavior: 'smooth'
				})
			}
		}
	}
}
</script>
